// Decides which feed items fold out of the thread and into a response's action
// bar. Pure (types only) so it lives here in shared/client and stays
// unit-testable; web consumes it from `components/messages/MessageList`.
import { isAutoMessageType, isResponseMessage, opensTurn } from "./anchoredBiscuits.ts";
import { messageIsBreadcrumb } from "./breadcrumbs.ts";
import type { RootItem } from "./eventTree.ts";

export type TurnBreadcrumbs = {
  /**
   * The feed items folded into each response's action bar, keyed by the
   * response's message id. Values are indices into the same `rootItems` array
   * that was passed in, chronological within each answer.
   */
  byResponseMessageId: ReadonlyMap<number, number[]>;
  /** Every root item the feed must not render at top level any more. */
  foldedIndices: ReadonlySet<number>;
  /**
   * The run in progress: folded items that have no answer yet, chronological.
   * Empty on an idle thread, where nothing is still being held.
   *
   * These are the same items `byResponseMessageId` will receive once okthx
   * replies. Until then they belong to the thinking indicator, which is the
   * only thing on screen standing in for the answer that has not landed.
   */
  runningIndices: readonly number[];
};

/** Nothing folded — the feed renders exactly as it did before. Also what a
 *  caller passes when the feature is gated off. */
export const NO_TURN_BREADCRUMBS: TurnBreadcrumbs = {
  byResponseMessageId: new Map(),
  foldedIndices: new Set(),
  runningIndices: [],
};

/**
 * Everything standing between a user's message and okthx's answer, folded into
 * that answer's action bar: the breadcrumb messages, and — when "show all
 * events" is on — the turn, cluster and orphan-event groups interleaved with
 * them. One toggle unrolls the lot, in feed order, so the trace reads the same
 * whichever view you are in.
 *
 * A run belongs to the answer that closes it, so the walk is: collect items, and
 * when a response lands, hand it everything collected since the last response or
 * user message.
 *
 * An item hides the moment it is collected, not when it finds an answer, so a
 * turn in progress shows no accumulating trace — the run appears all at once,
 * behind the toggle, when okthx actually replies. It is still reachable in the
 * meantime: what is pending when the walk ends is the run in progress, returned
 * as `runningIndices` for the thinking indicator to disclose.
 *
 * The exception is a run a new user message interrupts. It never got an answer
 * to fold into, so it comes back inline rather than staying hidden with no way
 * to reach it. `selectAnchoredBiscuits` releases held cards on the same
 * boundary, so a turn that ends without answering gives back its steps and its
 * cards together rather than one and not the other. That fallback is also what keeps threads whose messages predate
 * `userResponse` artifacts rendering at all: with no answer to anchor on, every
 * one of their items would otherwise hide itself and blank the thread.
 */
export function selectTurnBreadcrumbs(
  rootItems: RootItem[],
  { isTurnRunning }: { isTurnRunning: boolean },
): TurnBreadcrumbs {
  const byResponseMessageId = new Map<number, number[]>();
  const foldedIndices = new Set<number>();
  let pending: number[] = [];

  const releasePending = () => {
    for (const index of pending) foldedIndices.delete(index);
    pending = [];
  };

  rootItems.forEach((item, index) => {
    // Turn / cluster / orphan groups only exist in the event view, and they are
    // exactly the "how it happened" detail the breadcrumbs summarize — so they
    // travel with them.
    if (item.kind !== "message") {
      pending.push(index);
      foldedIndices.add(index);
      return;
    }

    const message = item.event.message;
    // An aborted message renders nothing, so it neither folds nor hosts.
    if (message.status === "aborted") return;

    if (opensTurn(message)) {
      // A new request opens a new turn; anything still pending never got an
      // answer, so give it back to the feed.
      releasePending();
      return;
    }

    // An auto-message records something the user did to okthx's card. It is not
    // a step on the way to an answer, so it does not fold; and it is not a new
    // request, so it does not end the run either. The turn carries on around it.
    if (isAutoMessageType(message.type)) return;

    if (isResponseMessage(message.artifacts ?? [])) {
      if (pending.length > 0) {
        byResponseMessageId.set(message.id, pending);
        pending = [];
      }
      return;
    }

    if (messageIsBreadcrumb(message)) {
      pending.push(index);
      foldedIndices.add(index);
    }
  });

  // Whatever is still pending has no answer yet. While a turn is working that is
  // the point — it surfaces when the turn replies, and until then the thinking
  // indicator discloses it. On an idle thread no reply is coming, so hiding it
  // would strand the trace with no toggle to reach it.
  const runningIndices = isTurnRunning ? pending : [];
  if (!isTurnRunning) releasePending();

  //
  // Shared empty value when nothing folded — see the same note in
  // `selectAnchoredBiscuits`; this result reaches the feed through the same
  // per-delta context.
  if (foldedIndices.size === 0) return NO_TURN_BREADCRUMBS;
  return { byResponseMessageId, foldedIndices, runningIndices };
}
